// MAKE IT YOURS — the first walk's two asks after the connection (2026-10-22):
// bring the project you are in, then your skills. Each is a prompt the person
// copies into the ai they just connected; the ai does the work over MCP, and the
// dashboard NOTICES what lands on its own, so netti can say so in the guide and
// hand over a way to open it. Either may already have happened in the chat (the
// invite asks the ai to offer both, and innernet_list_projects says so to an
// ai that finds an empty memory), so an arrival from earlier in the walk counts.
//
// How an arrival is heard:
//   a project  the shell's live project list (realtime on `projects`, a 6s poll
//              while walking) diffs in a new top-level folder and says
//              `innernet:project-arrived` {id, name, slug} (components/os/OsShell)
//   skills     a skill is a page with a `kind: skill` manifest, written with no
//              event of its own, so the walk counts them (GET /api/os/skills)
//              when it opens and again while the skills beat is up
//
// Pure helpers plus two tiny browser reads; the conductor does the rest.

import { PROMPT_PROJECT, PROMPT_SKILLS, PROMPT_SYNC } from "../onboarding/prompts";

export const PROJECT_ARRIVED_EVENT = "innernet:project-arrived";

export interface ArrivedProject {
  id: string;
  name: string;
  slug: string;
  /** when this page heard it (ms) */
  at: number;
}

/** the prompt a beat hands over (the standing instruction has no arrival to hear) */
export function bringPrompt(key: "project" | "skills" | "instruction"): string {
  return key === "project" ? PROMPT_PROJECT : key === "skills" ? PROMPT_SKILLS : PROMPT_SYNC;
}

/** the project that came in last since `since`: never the skills project (that is the next ask's) */
export function latestProject(arrivals: readonly ArrivedProject[], since: number): ArrivedProject | null {
  let best: ArrivedProject | null = null;
  for (const a of arrivals) {
    if (a.at < since || a.slug === "skills") continue;
    if (!best || a.at >= best.at) best = a;
  }
  return best;
}

/** how many skills came in, said the way netti says it */
export function skillsWord(n: number): string {
  return n === 1 ? "one skill" : `${n} skills`;
}

/** fill an arrival line: `{project}` and `{skills}` */
export function fillArrived(line: string, o: { project?: string; skills?: number }): string {
  return line
    .replace(/\{project\}/g, o.project ?? "your project")
    .replace(/\{skills\}/g, skillsWord(o.skills ?? 0));
}

/** say on the window that a project arrived (the shell calls this for each new top-level folder) */
export function announceProject(p: { id: string; name: string; slug: string }): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PROJECT_ARRIVED_EVENT, { detail: { id: p.id, name: p.name, slug: p.slug } }));
}

/** how many skills the person keeps right now, or null when it could not be read */
export async function readSkillCount(): Promise<number | null> {
  try {
    const res = await fetch("/api/os/skills", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    const body = (await res.json()) as { skills?: unknown };
    return Array.isArray(body.skills) ? body.skills.length : null;
  } catch {
    return null;
  }
}
